import { useCallback, useEffect, useRef, useState } from "react";
import type { LensId } from "../map/presets";
import { useT } from "../hooks/useLang";

/**
 * DemoTour — a foolproof, auto-driving guided walkthrough for live presentations
 * (built for the Yala City Municipality mayoral demo). It scripts a fixed
 * sequence of the dashboard's strongest, all-live views: each scene switches the
 * lens, flies the camera, and shows a Thai-first narration card. Autoplay so the
 * presenter never has to hunt for a toggle mid-pitch; manual prev/next/pause and
 * an exit are always one click away.
 *
 * Purely additive: it drives the existing lens + camera setters via callbacks
 * and renders an overlay. Nothing else in the app needs to know it exists.
 */

export interface TourScene {
  id: string;
  lensId: LensId;
  lng: number;
  lat: number;
  zoom: number;
  titleEn: string;
  titleTh: string;
  bodyEn: string;
  bodyTh: string;
}

interface Props {
  scenes: TourScene[];
  /** Apply a scene: caller switches lens + flies the camera. */
  onScene: (scene: TourScene) => void;
  onExit: () => void;
  /** Seconds each scene holds before auto-advancing. */
  dwellSeconds?: number;
}

const DEFAULT_DWELL = 9;

export function DemoTour({ scenes, onScene, onExit, dwellSeconds = DEFAULT_DWELL }: Props) {
  const t = useT();
  // Respect prefers-reduced-motion: the autoplay + camera flights are a vestibular
  // irritant. Default to paused so the operator has to explicitly start the tour
  // (the DEMO button in the top bar still works, and the next/prev/exit keys
  // are always available).
  const [playing, setPlaying] = useState(
    () => typeof window === "undefined"
      ? true
      : !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
  );
  const [index, setIndex] = useState(0);
  // Progress 0→1 within the current scene, for the dwell bar.
  const [progress, setProgress] = useState(0);
  const onSceneRef = useRef(onScene);
  onSceneRef.current = onScene;

  const total = scenes.length;
  const scene = scenes[index];

  // Apply the scene whenever the index changes.
  useEffect(() => {
    if (scene) onSceneRef.current(scene);
    setProgress(0);
  }, [index, scene]);

  // Autoplay dwell timer — ticks progress, advances (and loops) at the end.
  useEffect(() => {
    if (!playing) return;
    const stepMs = 100;
    const ticks = (dwellSeconds * 1000) / stepMs;
    let elapsed = 0;
    const id = window.setInterval(() => {
      elapsed += 1;
      setProgress(Math.min(1, elapsed / ticks));
      if (elapsed >= ticks) {
        elapsed = 0;
        setIndex((i) => (i + 1) % total);
      }
    }, stepMs);
    return () => window.clearInterval(id);
  }, [playing, index, dwellSeconds, total]);

  // Keyboard: ← → navigate, space play/pause, Esc exit.
  const go = useCallback((delta: number) => {
    setIndex((i) => (i + delta + total) % total);
  }, [total]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") { e.preventDefault(); go(1); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); go(-1); }
      else if (e.key === " ") { e.preventDefault(); setPlaying((p) => !p); }
      else if (e.key === "Escape") { e.preventDefault(); onExit(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, onExit]);

  if (!scene) return null;

  return (
    <div className="demo-tour" role="dialog" aria-label={t("Guided demo", "ทัวร์นำเสนอ")}>
      <div className="demo-tour-card">
        <div className="demo-tour-top">
          <span className="demo-tour-counter mono">
            {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
          </span>
          <span className="demo-tour-kicker mono">{t("GUIDED TOUR", "นำเสนออัตโนมัติ")}</span>
          <button className="demo-tour-exit mono" onClick={onExit} aria-label={t("Exit tour", "ออกจากการนำเสนอ")}>
            ✕ {t("EXIT", "ออก")}
          </button>
        </div>

        <h2 className="demo-tour-title">{t(scene.titleEn, scene.titleTh)}</h2>
        <p className="demo-tour-body">{t(scene.bodyEn, scene.bodyTh)}</p>

        <div className="demo-tour-progress" aria-hidden="true">
          <div className="demo-tour-progress-fill" style={{ width: `${progress * 100}%` }} />
        </div>

        <div className="demo-tour-controls">
          <button className="mono" onClick={() => go(-1)} aria-label={t("Previous", "ก่อนหน้า")}>‹ {t("PREV", "ก่อนหน้า")}</button>
          <button className="mono demo-tour-play" onClick={() => setPlaying((p) => !p)} aria-label={playing ? t("Pause", "หยุด") : t("Play", "เล่น")}>
            {playing ? `⏸ ${t("PAUSE", "หยุด")}` : `▶ ${t("PLAY", "เล่น")}`}
          </button>
          <button className="mono" onClick={() => go(1)} aria-label={t("Next", "ถัดไป")}>{t("NEXT", "ถัดไป")} ›</button>
        </div>

        <div className="demo-tour-dots" role="tablist" aria-label={t("Scenes", "ฉาก")}>
          {scenes.map((s, i) => (
            <button
              key={s.id}
              className={`demo-tour-dot ${i === index ? "active" : ""}`}
              onClick={() => setIndex(i)}
              role="tab"
              aria-selected={i === index}
              aria-label={t(s.titleEn, s.titleTh)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
